<template>
  <FreePageLayout>
    <div class="absolute inset-x-0 z-10 flex items-center justify-between p-4">
      <div
        class="bg-static-black/20 flex h-8 w-8 items-center justify-center rounded-full"
      >
        <FreeBackButton
          class="back-button"
          @click="
            goBack({
              query: {
                source: 'special_offer_details',
                sourceElement: 'back_button',
              },
            })
          "
        />
      </div>
    </div>
    <Transition name="sticky-button">
      <div
        v-if="showStickyBackButton"
        class="bg-background-neutral-secondary border-situational-lightgrey card-shadow fixed left-0 z-50 flex w-full items-center gap-4 border-b px-4 py-2"
      >
        <div class="flex h-8 w-8 items-center justify-center">
          <FreeBackButton
            @click="
              goBack({
                query: {
                  source: 'special_offer_details',
                  sourceElement: 'back_button',
                },
              })
            "
          />
        </div>
        <p class="text-text-neutral-primary font-semibold">
          {{ currentSpecial.title }}
        </p>
      </div>
    </Transition>

    <div v-if="isSpecialsLoading" class="fixed top-0 z-40">
      <FullScreenLoader />
    </div>

    <div v-else class="relative -mx-4 w-screen">
      <div
        class="absolute inset-0 w-full bg-cover bg-no-repeat"
        :style="{ backgroundImage: `url(${currentSpecial?.image_url})` }"
      ></div>

      <div
        class="absolute inset-0 w-full"
        style="
          background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0) 0%,
            #000000 100%
          );
        "
      ></div>

      <div
        class="relative flex min-h-96 w-full flex-col justify-end space-y-1 p-6 pb-12 text-white"
      >
        <h1 class="text-3xl font-bold">{{ currentSpecial.title }}</h1>
        <p class="text-sm text-white/80">{{ currentSpecial.subtitle }}</p>
      </div>
    </div>

    <div
      v-if="!isSpecialsLoading"
      class="bg-background-neutral-primary relative z-10 -ml-4 -mt-7 w-screen rounded-t-3xl p-4 pb-24"
    >
      <div ref="specialOfferDetailsRef" class="w-full">
        <SpecialOfferDetails
          v-if="currentSpecial"
          :special="currentSpecial"
          @onTicketLinkClick="handleTicketLinkClick"
        />
      </div>

      <SpecialOfferOffersSection
        :offers="offers"
        :is-offers-loading="isOffersLoading"
        :current-special-offer="currentSpecial"
        @onCashbackClick="bottomSheet.show(cashbackBottomSheetName)"
        @onShowAllOffers="handleShowAllOffers"
      />

      <SearchFormBottomSheet
        :name="searchFormBottomSheetName"
        :dep-city="origCityName"
        :current-special="currentSpecial"
      />
      <CityOffersBottomSheet
        :name="cityOffersBottomSheetName"
        :city-name="selectedCityName"
        :offers="selectedCityOffers"
        :is-loading="false"
        :current-special-offer="currentSpecial"
        @onCashbackClick="bottomSheet.show(cashbackBottomSheetName)"
      />
      <CashbackBottomSheet :name="cashbackBottomSheetName" />

      <div class="fixed bottom-0 left-0 w-full p-4 pb-8">
        <FreeButton
          type="button"
          class="green-main-button w-full"
          @click="bottomSheet.show(searchFormBottomSheetName)"
        >
          {{ $t('specialOffer.findAviaTickets') }}
        </FreeButton>
      </div>
    </div>
  </FreePageLayout>
</template>

<script lang="ts" setup>
  import { computed, onMounted, ref, onUnmounted, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import {
    FreeBackButton,
    FreePageLayout,
    FullScreenLoader,
    CashbackBottomSheet,
    FreeButton,
    freedomApp,
    SupportedMiniApps,
  } from 'freedom-shared';
  import { controllerModel } from 'shared';

  import { specialOffersModel } from '@/features/special-offers';

  import {
    useSpecialOffers,
    useSpecialOfferDetail,
  } from '@/entities/special-offers';
  import type { hotOffersTypes } from '@/entities/hot-offers';

  import { amplitudeEventsLib } from '@/shared/lib';
  import { useBackNavigation } from '@/shared/model';

  import SpecialOfferDetails from './ui/SpecialOfferDetails.vue';
  import SpecialOfferOffersSection from './ui/SpecialOfferOffersSection.vue';
  import SearchFormBottomSheet from './ui/SearchFormBottomSheet.vue';
  import CityOffersBottomSheet from './ui/CityOffersBottomSheet.vue';

  const route = useRoute();
  const { goBack } = useBackNavigation();

  const cashbackBottomSheetName = 'cashback-bottom-sheet';
  const searchFormBottomSheetName = 'search-form-bottom-sheet';
  const cityOffersBottomSheetName = 'city-offers-bottom-sheet';
  const bottomSheet = controllerModel.useBottomSheet();

  const { origCityCode: depCityCode, origCityName } =
    specialOffersModel.useSpecialOffers();
  const specialId = computed(() => String(route.params.offerId || ''));
  const source = computed(() => route.query.source || 'main');

  const { offers, isLoading: isOffersLoading } = useSpecialOfferDetail({
    id: specialId,
    depCityCode,
  });

  const { specials, isLoading: isSpecialsLoading } = useSpecialOffers({
    depCityCode,
  });

  const currentSpecial = computed(() =>
    specials.value.find(s => s.id === specialId.value),
  );

  const selectedCityName = ref<string>('');
  const selectedCityOffers = ref<hotOffersTypes.MappedHotOffer[]>([]);
  const showStickyBackButton = ref(false);
  const specialOfferDetailsRef = ref<HTMLElement>();

  const handleShowAllOffers = (
    cityName: string,
    cityOffers: hotOffersTypes.MappedHotOffer[],
  ) => {
    selectedCityName.value = cityName;
    selectedCityOffers.value = cityOffers;
    bottomSheet.show(cityOffersBottomSheetName);
  };

  const handleTicketLinkClick = () => {
    if (currentSpecial.value?.event_tickets_url) {
      if (currentSpecial.value?.event_tickets_url.includes('ticketon')) {
        freedomApp.lib.openMiniApp(
          SupportedMiniApps.TICKETON,
          currentSpecial.value.event_tickets_url,
        );
      } else {
        freedomApp.lib.navigate(currentSpecial.value?.event_tickets_url);
      }
    }
  };

  const handleScroll = () => {
    if (!specialOfferDetailsRef.value) return;

    const rect = specialOfferDetailsRef.value.getBoundingClientRect();
    showStickyBackButton.value = rect.top < 0;
  };

  onMounted(() => {
    window.addEventListener('scroll', handleScroll);
    if (currentSpecial.value) {
      amplitudeEventsLib.specialOffersBannerOpened({
        bannerId: currentSpecial.value.id,
        bannerName: currentSpecial.value.title,
        bannerType:
          currentSpecial.value.type === 'EVENT' ? 'events' : 'flights',
        source: source.value as 'main' | 'all_special_offers',
      });
    }
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
  });

  watch(isOffersLoading, newVal => {
    if (newVal) {
      showStickyBackButton.value = false;
    }
  });
</script>

<style scoped>
  .back-button {
    --return-button-color: theme('colors.static.white.DEFAULT');
  }

  .sticky-button-enter-active,
  .sticky-button-leave-active {
    transition: all 0.3s ease;
  }

  .sticky-button-enter-from,
  .sticky-button-leave-to {
    opacity: 0;
  }
</style>
